// Menu / catalog selectors.
//
// The active store is whichever store the user is currently browsing:
//   - in multi-store QR, that's filterReducer.selected_store.store_id
//   - in single-store QR, fall back to sessionReducer.store_details.store_id,
//     then to the legacy sessionReducer?.store_details.store_id
//
// Components should never reach into catalogReducer.by_store directly — go
// through useActiveStoreCatalog so the active-store resolution stays in one
// place.

import { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

const EMPTY_STORE = {
    items: [],
    categories: [],
    addons: [],
    labels: [],
    item_variants: [],
};

export const useActiveStoreId = () =>
    useSelector((s) => {
        const fromFilter  = s.filterReducer?.selected_store?.store_id;
        const fromSession = s.sessionReducer?.store_details?.store_id;
        const fromLegacy  = s.sessionReducer?.store_details?.store_id;
        return fromFilter || fromSession || fromLegacy || null;
    });

export const useActiveStoreCatalog = () => {
    const storeId = useActiveStoreId();
    
    // Get the raw catalog (unmodified base prices) from the store
    const rawCatalog = useSelector(
        (s) => (storeId ? s.catalogReducer?.by_store?.[String(storeId)] : null) || EMPTY_STORE,
        shallowEqual,
    );

    // Get the active order type to determine channel_plu
    const activeOrderCode = useSelector(
        (s) => {
            let code = s.cartReducer?.orderType?.order?.order_code;
            if (!code) {
                const locId = s.sessionReducer?.location_id;
                if (locId?.toLowerCase() === "ta") code = "TA";
                else if (locId) code = "DI";
            }
            return code || "";
        }
    );

    // Compute the dynamically priced catalog
    return useMemo(() => {
        if (!rawCatalog?.items?.length) return rawCatalog;

        const applyPrice = (item) => {
            const channelPlu = item.channel_plu?.find(w => w?.channel_code?.toLowerCase() === activeOrderCode?.toLowerCase());
            const val = parseFloat(channelPlu?.plu || 0);
            return {
                ...item,
                item_variant: (item.item_variant || []).map(v => ({ 
                    ...v, 
                    plu: (parseFloat(v.plu || 0) + val).toFixed(2) 
                }))
            };
        };

        const isActive = (obj) => obj && obj.status !== false && obj.status !== "false";
        const inactiveCategoryIds = new Set(
            (rawCatalog.categories || [])
                .filter((cat) => !isActive(cat))
                .map((cat) => String(cat?.category_id || cat?._id))
        );
        const isItemActive = (item) => {
            if (!isActive(item)) return false;
            if (item.category_id && inactiveCategoryIds.has(String(item.category_id))) return false;
            return true;
        };

        const activeItems = (rawCatalog.items || []).filter(isItemActive);
        const activeVariants = (rawCatalog.item_variants || []).filter(isActive);
        const activeCategories = (rawCatalog.categories || []).filter(isActive);
        const activeAddons = (rawCatalog.addons || []).filter(isActive).map((addon) => ({
            ...addon,
            addon_item: (addon.addon_item || []).filter(isActive),
        }));
        const activeLabels = (rawCatalog.labels || []).filter(isActive);

        return {
            ...rawCatalog,
            items: activeItems.map(applyPrice),
            item_variants: activeVariants.map(applyPrice),
            categories: activeCategories,
            addons: activeAddons,
            labels: activeLabels,
        };
    }, [rawCatalog, activeOrderCode]);
};

export const useIsStoreLoaded = (storeId) =>
    useSelector(
        (s) => !!storeId && (s.catalogReducer?.loaded_stores || []).includes(String(storeId)),
    );

export const useDietaryOptions = () => {
    const { items } = useActiveStoreCatalog();
    return useMemo(() => {
        const set = new Set();
        items.forEach((it) => {
            if (it?.dietary) set.add(String(it.dietary).toLowerCase());
        });
        return Array.from(set);
    }, [items]);
};

const matchesCategoryFilter = (item, category) => {
    if (!category?.obj) return true;
    const { type, obj } = category;
    if (obj?.master_category_id === 'All_Item') return true;
    if (type === 'MasterCategory') return item?.master_category_id === obj.master_category_id;
    if (type === 'category')        return item?.category_id        === obj.category_id;
    if (type === 'label')           return Array.isArray(item?.tag) && item.tag.includes(obj.name);
    return true;
};

const sortItems = (items, sortBy) => {
    if (sortBy !== 'low' && sortBy !== 'high') return items;
    const out = [...items];
    out.sort((a, b) => {
        const pa = parseFloat(a?.item_variant?.[0]?.plu || 0);
        const pb = parseFloat(b?.item_variant?.[0]?.plu || 0);
        return sortBy === 'low' ? pa - pb : pb - pa;
    });
    return out;
};

const sortBySequence = (items) =>
    [...items].sort((a, b) => (a?.sequence_no || 0) - (b?.sequence_no || 0));

// Returns the filtered + sorted list of items for the current menu view.
// Memoized internally so it only recomputes when inputs change.
export const useFilteredItems = () => {
    const { items } = useActiveStoreCatalog();
    const category       = useSelector((s) => s.filterReducer?.category);
    const searchQuery    = useSelector((s) => s.filterReducer?.searchQuery || '');
    const dietaryFilter  = useSelector((s) => s.filterReducer?.dietaryFilter || [], shallowEqual);
    const sortBy         = useSelector((s) => s.filterReducer?.sortBy || '');

    return useMemo(() => {
        let list = items.filter((it) => matchesCategoryFilter(it, category));
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            list = list.filter((it) => it?.item_name?.toLowerCase().includes(q));
        }
        if (dietaryFilter.length) {
            list = list.filter((it) => {
                const itemDietary = String(it?.dietary || '').toLowerCase().trim();
                return dietaryFilter.some((filter) => {
                    const f = String(filter || '').toLowerCase().trim();
                    if (f === "non-veg" || f === "nonveg" || f === "non veg" || f === "non_veg") {
                        return itemDietary === "non-veg" || itemDietary === "nonveg" || itemDietary === "non veg" || itemDietary === "non_veg" || itemDietary === "egg" || itemDietary === "eggetarian" || itemDietary === "eggiterian" || itemDietary === "eggitarian";
                    }
                    return itemDietary === f;
                });
            });
        }
        // Keep items with a valid price (including 0rs)
        list = list.filter((it) => parseFloat(it?.item_variant?.[0]?.plu) >= 0);
        list = sortBySequence(list);
        list = sortItems(list, sortBy);
        return list;
    }, [items, category, searchQuery, dietaryFilter, sortBy]);
};
